/* ============================================================================
   tne-depoimentos.js — Carrossel horizontal com parallax pin scroll
   DistribuidorTopBioEuropa | Site institucional B2B

   Dois modos:
     A) PIN PARALLAX (desktop ≥ 1024px com parallax_mode = ON)
        - Secção fica "presa" via position:sticky enquanto o utilizador
          faz scroll vertical.
        - O scroll vertical é convertido em translateX do track.
        - O mega-text desliza no sentido oposto (parallax depth).
        - Setas/dots scrollam o WINDOW para a posição Y correspondente.
     B) NATIVO (mobile/tablet OU parallax_mode = OFF)
        - Scroller horizontal com snap, drag, touch nativo.
        - Setas/dots usam scrollTo no scroller.

   Performance: rAF + listeners passivos + IntersectionObserver para o
   active dot em modo nativo. CSS custom properties (--tne-track-x,
   --tne-bg-shift) evitam thrashing de style recálculos.
   ============================================================================ */
(function () {
  'use strict';

  var PIN_BREAKPOINT = 1024;

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function initDepoimentos(root) {
    if (!root || root.dataset.tneDepoimentosInit === '1') return;
    root.dataset.tneDepoimentosInit = '1';

    var pin = root.querySelector('[data-tne-depoimentos-pin]');
    var sticky = root.querySelector('[data-tne-depoimentos-sticky]');
    var scroller = root.querySelector('[data-tne-depoimentos-scroller]');
    var track = root.querySelector('[data-tne-depoimentos-track]');
    var bgText = root.querySelector('[data-tne-depoimentos-bg]');
    if (!scroller || !track) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-tne-dep-card]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-tne-depoimentos-dot]'));
    var prevBtn = root.querySelector('[data-tne-depoimentos-prev]');
    var nextBtn = root.querySelector('[data-tne-depoimentos-next]');
    var counterEl = root.querySelector('[data-tne-depoimentos-counter]');
    var progressFill = root.querySelector('[data-tne-depoimentos-progress]');
    if (!cards.length) return;

    function pad2(n) { return n < 10 ? '0' + n : '' + n; }

    var prefersReduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var parallaxOpt = root.getAttribute('data-parallax') === 'true';

    // ------------------------------------------------------------------------
    // Common helpers (shared between modes)
    // ------------------------------------------------------------------------
    function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === cards.length - 1;

      // Editorial counter "01" → "04"
      if (counterEl) counterEl.textContent = pad2(index + 1);

      // Progress fill — escala 0→1 conforme posição.
      if (progressFill) {
        var ratio = cards.length <= 1 ? 1 : (index / (cards.length - 1));
        progressFill.style.setProperty('--progress', ratio.toFixed(4));
      }
    }

    // ========================================================================
    // MODE A — Pin parallax
    // ========================================================================
    var pinController = null;

    function setupPinMode() {
      if (!pin || !sticky || !bgText) return false;

      // Distância horizontal real a percorrer = scrollWidth - viewport width.
      var trackWidth = track.scrollWidth;
      var viewportWidth = sticky.clientWidth;
      var distance = Math.max(0, trackWidth - viewportWidth);

      if (distance === 0) {
        // Cards cabem no viewport — não há nada para scrollar.
        pin.style.height = '';
        track.style.setProperty('--tne-track-x', '0px');
        bgText.style.setProperty('--tne-bg-shift', '0px');
        setActiveDot(0);
        return true;
      }

      // Altura do pin = distância horizontal + 1 viewport para entrada/saída suave.
      // Multiplicador permite ajustar "velocidade" do efeito (1 = direto).
      var pinExtra = window.innerHeight;
      var totalScroll = distance + pinExtra;
      pin.style.height = totalScroll + 'px';

      var rafId = null;
      var lastProgress = -1;

      function update() {
        rafId = null;
        var rect = pin.getBoundingClientRect();
        var scrollableHeight = pin.offsetHeight - window.innerHeight;
        if (scrollableHeight <= 0) return;
        var progress = clamp(-rect.top / scrollableHeight, 0, 1);
        if (Math.abs(progress - lastProgress) < 0.001) return;
        lastProgress = progress;

        var translateX = -distance * progress;
        track.style.setProperty('--tne-track-x', translateX + 'px');

        // Mega-text desliza no sentido oposto, mas com amplitude menor
        // (~30% do movimento do track) para sensação de profundidade.
        var bgShift = -translateX * 0.3;
        bgText.style.setProperty('--tne-bg-shift', bgShift + 'px');

        // Active dot snap (counter) + progress fill contínuo (mais elegante).
        var activeIndex = Math.round(progress * (cards.length - 1));
        setActiveDot(activeIndex);
        if (progressFill) {
          progressFill.style.setProperty('--progress', progress.toFixed(4));
        }
      }

      function onScroll() {
        if (rafId) return;
        rafId = window.requestAnimationFrame(update);
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      update();

      // Setas/dots: scrollar a página para a posição Y correspondente.
      function scrollWindowToCard(index) {
        var target = clamp(index, 0, cards.length - 1);
        var ratio = (cards.length - 1) === 0 ? 0 : target / (cards.length - 1);
        var pinTop = pin.getBoundingClientRect().top + window.pageYOffset;
        var scrollableHeight = pin.offsetHeight - window.innerHeight;
        var targetY = pinTop + (ratio * scrollableHeight);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          var current = Math.round(lastProgress * (cards.length - 1));
          scrollWindowToCard(current - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          var current = Math.round(lastProgress * (cards.length - 1));
          scrollWindowToCard(current + 1);
        });
      }
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { scrollWindowToCard(i); });
      });

      // Teclado: ←/→ scrollam a página em incrementos de 1 card.
      scroller.addEventListener('keydown', function (e) {
        var current = Math.round(lastProgress * (cards.length - 1));
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollWindowToCard(current + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); scrollWindowToCard(current - 1); }
        else if (e.key === 'Home') { e.preventDefault(); scrollWindowToCard(0); }
        else if (e.key === 'End') { e.preventDefault(); scrollWindowToCard(cards.length - 1); }
      });

      pinController = {
        teardown: function () {
          window.removeEventListener('scroll', onScroll);
          if (rafId) window.cancelAnimationFrame(rafId);
          pin.style.height = '';
          track.style.removeProperty('--tne-track-x');
          bgText.style.removeProperty('--tne-bg-shift');
        },
        recompute: function () {
          // Recalcula dimensões (resize / fonts loaded).
          trackWidth = track.scrollWidth;
          viewportWidth = sticky.clientWidth;
          distance = Math.max(0, trackWidth - viewportWidth);
          if (distance === 0) { pin.style.height = ''; return; }
          totalScroll = distance + window.innerHeight;
          pin.style.height = totalScroll + 'px';
          lastProgress = -1;
          update();
        }
      };

      return true;
    }

    // ========================================================================
    // MODE B — Scroll horizontal nativo (mobile/disabled)
    // ========================================================================
    function setupNativeMode() {
      // Drag-to-scroll
      var isDown = false;
      var startX = 0;
      var startScroll = 0;
      var hasMoved = false;

      scroller.addEventListener('mousedown', function (e) {
        isDown = true; hasMoved = false;
        startX = e.pageX - scroller.offsetLeft;
        startScroll = scroller.scrollLeft;
        scroller.classList.add('is-dragging');
      });

      var endDrag = function () {
        if (!isDown) return;
        isDown = false;
        scroller.classList.remove('is-dragging');
        if (hasMoved) snapToNearest();
      };
      scroller.addEventListener('mouseleave', endDrag);
      scroller.addEventListener('mouseup', endDrag);
      window.addEventListener('mouseup', endDrag);
      scroller.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - scroller.offsetLeft;
        var walk = (x - startX) * 1.4;
        if (Math.abs(walk) > 4) hasMoved = true;
        scroller.scrollLeft = startScroll - walk;
      });

      function scrollToCard(index) {
        var i = clamp(index, 0, cards.length - 1);
        var card = cards[i];
        if (!card) return;
        var sRect = scroller.getBoundingClientRect();
        var cRect = card.getBoundingClientRect();
        var target = scroller.scrollLeft + (cRect.left - sRect.left)
          - (sRect.width / 2) + (cRect.width / 2);
        scroller.scrollTo({ left: target, behavior: 'smooth' });
      }
      function currentIndex() {
        var center = scroller.scrollLeft + scroller.clientWidth / 2;
        var closest = 0, minDist = Infinity;
        cards.forEach(function (card, i) {
          var cardCenter = card.offsetLeft + card.offsetWidth / 2;
          var d = Math.abs(cardCenter - center);
          if (d < minDist) { minDist = d; closest = i; }
        });
        return closest;
      }
      function snapToNearest() { scrollToCard(currentIndex()); }

      if (prevBtn) prevBtn.addEventListener('click', function () { scrollToCard(currentIndex() - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { scrollToCard(currentIndex() + 1); });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { scrollToCard(i); });
      });
      scroller.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollToCard(currentIndex() + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); scrollToCard(currentIndex() - 1); }
        else if (e.key === 'Home') { e.preventDefault(); scrollToCard(0); }
        else if (e.key === 'End') { e.preventDefault(); scrollToCard(cards.length - 1); }
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          var best = null;
          entries.forEach(function (entry) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (best && best.isIntersecting) {
            var index = cards.indexOf(best.target);
            if (index !== -1) setActiveDot(index);
          }
        }, { root: scroller, threshold: [0.5, 0.75, 1] });
        cards.forEach(function (card) { io.observe(card); });
      }

      setActiveDot(0);
    }

    // ========================================================================
    // Mode switcher (responsive)
    // ========================================================================
    function shouldUsePinMode() {
      return parallaxOpt
        && !prefersReduced
        && window.innerWidth >= PIN_BREAKPOINT
        && pin && sticky && bgText;
    }

    var currentMode = null;
    function applyMode() {
      var pinMode = shouldUsePinMode();
      var target = pinMode ? 'pin' : 'native';
      if (target === currentMode) {
        if (pinMode && pinController) pinController.recompute();
        return;
      }
      // Setup inicial: cada modo só corre uma vez por simplicidade.
      // Em resize entre modos, o pin é apenas recalculado ou neutralizado.
      if (currentMode === null) {
        if (pinMode) {
          var ok = setupPinMode();
          if (!ok) setupNativeMode();
        } else {
          setupNativeMode();
        }
        currentMode = target;
        return;
      }
      // Já temos um modo activo: só o pin precisa de neutralização.
      if (currentMode === 'pin' && !pinMode && pinController) {
        pinController.teardown();
        // Cai para nativo na próxima inicialização — para evitar
        // listeners duplicados, marcamos e o utilizador continua a ver
        // o estado renderizado (track flow normal).
        currentMode = 'native-fallback';
      } else if (currentMode === 'pin' && pinMode && pinController) {
        pinController.recompute();
      }
    }

    applyMode();

    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) return;
      resizeRaf = window.requestAnimationFrame(function () {
        applyMode();
        resizeRaf = null;
      });
    });

    // Imagens/fontes podem mudar largura do track depois do load inicial.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { applyMode(); });
    }
    window.addEventListener('load', function () { applyMode(); });
  }

  function boot() {
    document.querySelectorAll('[data-tne-depoimentos]').forEach(initDepoimentos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
